import React, { useState } from 'react';
import { Vehicle, VehicleStatus, VehicleAvailability } from '../../types/vehicle';
import VehicleStatusBadge from './VehicleStatusBadge';
import VehicleAvailabilityModal from './VehicleAvailabilityModal';

interface VehicleCardViewProps {
  vehicles: Vehicle[];
  onVehicleSelect?: (vehicle: Vehicle) => void;
  onVehicleEdit?: (vehicle: Vehicle) => void;
  loading?: boolean;
  availability?: VehicleAvailability[];
}

const VehicleCardView: React.FC<VehicleCardViewProps> = ({
  vehicles,
  onVehicleSelect,
  onVehicleEdit,
  loading = false,
  availability = []
}) => {
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState<VehicleAvailability | null>(null);
  const getStatusColor = (status: VehicleStatus): string => {
    const colors: Record<string, string> = {
      available: 'from-green-50 to-emerald-50',
      rented: 'from-purple-50 to-indigo-50',
      maintenance: 'from-orange-50 to-amber-50',
      'out-of-service': 'from-red-50 to-rose-50',
      reserved: 'from-gray-50 to-slate-50'
    };
    return colors[status] || 'from-gray-50 to-slate-50';
  };

  const isMaintenanceDue = (vehicle: Vehicle): boolean => {
    if (!vehicle.nextServiceDue) return false;
    const dueDate = new Date(vehicle.nextServiceDue);
    const today = new Date();
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 7;
  };

  // Get availability info for a vehicle
  const getVehicleAvailability = (vehicleId: string) => {
    return availability.find(a => a.carId === vehicleId);
  };

  // Handle opening availability modal
  const handleViewAvailability = (vehicleId: string) => {
    const vehicleAvail = getVehicleAvailability(vehicleId);
    if (vehicleAvail) {
      setSelectedAvailability(vehicleAvail);
      setShowAvailabilityModal(true);
    }
  };

  // Format date for display
  const formatNextAvailableDate = (date: Date | null | string) => {
    if (!date) return null;
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    }
    if (d.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
            <div className="h-48 bg-gray-200"></div>
            <div className="p-6 space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="text-center py-16">
        <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No vehicles found</h3>
        <p className="mt-2 text-sm text-gray-500">Try adjusting your filters or add a new vehicle.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {vehicles.map((vehicle) => {
        const total = vehicle.totalQuantity || 0;
        const vehicleAvail = getVehicleAvailability(vehicle._id);
        const activeBookings = vehicleAvail?.activeBookings || vehicle.activeBookings || 0;
        const available = Math.max(0, total - activeBookings);
        const maintenanceDue = isMaintenanceDue(vehicle);
        const nextAvailDate = vehicleAvail?.nextAvailableDate;
        const upcomingReturns = vehicleAvail?.upcomingReturns || [];
        
        // Get 7+ days rate
        const limitedRate = vehicle.rates?.find(tier => tier.minDays === 7);
        const unlimitedRate = vehicle.unlimedRates?.find(tier => tier.minDays === 7);

        return (
          <div
            key={vehicle._id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden group"
          >
            {/* Header with gradient based on status */}
            <div className={`bg-gradient-to-r ${getStatusColor(vehicle.status)} px-6 py-4 border-b border-gray-200`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{vehicle.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {vehicle.brand} {vehicle.model} ({vehicle.year})
                  </p>
                  {vehicle.licensePlate && (
                    <p className="text-xs text-gray-500 mt-1">📋 {vehicle.licensePlate}</p>
                  )}
                </div>
                <VehicleStatusBadge status={vehicle.status} />
              </div>
            </div>

            {/* Vehicle Image Placeholder */}
            <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              {vehicle.images && vehicle.images.length > 0 ? (
                <img
                  src={vehicle.images[0].url}
                  alt={vehicle.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <svg className="w-24 h-24 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                </svg>
              )}
              {maintenanceDue && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Service Due
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* Location */}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>{vehicle.location || 'Not specified'}</span>
              </div>

              {/* Availability with Next Available Date */}
              <div 
                className={`bg-gray-50 rounded-lg p-3 ${upcomingReturns.length > 0 ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                onClick={() => upcomingReturns.length > 0 && handleViewAvailability(vehicle._id)}
                title={upcomingReturns.length > 0 ? "Click to view booking schedule" : undefined}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Availability</span>
                  <span className={`text-lg font-bold ${available > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {available}/{total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      available > 0 ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${total > 0 ? (available / total) * 100 : 0}%` }}
                  ></div>
                </div>
                {available > 0 ? (
                  <div className="mt-1">
                    <p className="text-xs text-green-600 font-medium">
                      ✓ {available} available now
                    </p>
                    {upcomingReturns.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium hover:underline">View booking schedule</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="text-xs text-red-600 font-medium">All units booked</p>
                    {nextAvailDate && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium hover:underline">Next: {formatNextAvailableDate(nextAvailDate)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Upcoming Returns Preview */}
                {upcomingReturns.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Upcoming returns:</p>
                    <div className="space-y-1">
                      {upcomingReturns.slice(0, 2).map((ret, idx) => (
                        <div key={idx} className="text-xs text-gray-600 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                          <span>{formatNextAvailableDate(ret.returnDate)}</span>
                        </div>
                      ))}
                      {upcomingReturns.length > 2 && (
                        <div className="text-xs text-blue-600 font-medium">
                          +{upcomingReturns.length - 2} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-gray-600 mb-2 font-medium">7+ Days Rate</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-gray-500">Limited</p>
                    <p className="text-sm font-bold text-blue-600">
                      ${limitedRate?.rate || 'N/A'}/day
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Unlimited</p>
                    <p className="text-sm font-bold text-blue-600">
                      ${unlimitedRate?.rate || 'N/A'}/day
                    </p>
                  </div>
                </div>
              </div>

              {/* Performance Stats */}
              {(vehicle.totalBookings || vehicle.totalRevenue) && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500">Total Trips</p>
                    <p className="text-lg font-semibold text-gray-900">{vehicle.totalBookings || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Revenue</p>
                    <p className="text-lg font-semibold text-gray-900">
                      ${vehicle.totalRevenue?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              )}

              {/* Vehicle Specs */}
              <div className="flex items-center gap-4 text-xs text-gray-600 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                  <span>{vehicle.seats} seats</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                  </svg>
                  <span>{vehicle.bags} bags</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                  <span>{vehicle.isAutomatic ? 'Auto' : 'Manual'}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => onVehicleSelect?.(vehicle)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                View Details
              </button>
              <button
                onClick={() => onVehicleEdit?.(vehicle)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                title="Edit Vehicle"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>
          </div>
        );
      })}

      {/* Availability Modal */}
      <VehicleAvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        availability={selectedAvailability}
      />
    </div>
  );
};

export default VehicleCardView;
